import { PanelType } from '@/types/pulse';
import { normalizeForRespondentPanel } from '@/utils/pulse/panels';
import { Respondent } from '@/types/pulsedb';
import type { RespondentDraft } from '@/utils/pulse/respondentImport';
import { SupabaseClient } from '@supabase/supabase-js';
import { getPanelistIdMatch } from '@/utils/pulse/queryParams';
import { getOrCreateRespondent } from './interviewEvents';
import { getInterviewWorkspaceId } from './interviews';

export const getRespondents = async (
  supabase: SupabaseClient,
  workspaceId: string
) => {
  const { data } = await supabase
    .from('respondents')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('deleted', false)
    .throwOnError();
  return data as unknown as Respondent[] | null;
};

/**
 * Columns the respondents table's search box can be pointed at, keyed by the
 * value carried in the URL. One column is searched at a time — the box asks
 * "find me this person", not "narrow this list on several axes at once".
 */
const RESPONDENT_SEARCH_COLUMNS = {
  id: 'panelist_id',
  name: 'full_name',
  email: 'email',
  phone: 'phone_no'
} as const;

export type RespondentSearchField = keyof typeof RESPONDENT_SEARCH_COLUMNS;

/** An id search is the default, so a bare `?search=` keeps its old meaning. */
export const DEFAULT_RESPONDENT_SEARCH_FIELD: RespondentSearchField = 'id';

export const parseRespondentSearchField = (
  value?: string | null
): RespondentSearchField =>
  value && Object.hasOwn(RESPONDENT_SEARCH_COLUMNS, value)
    ? (value as RespondentSearchField)
    : DEFAULT_RESPONDENT_SEARCH_FIELD;

export const getRespondentsPaginated = async (
  supabase: SupabaseClient,
  workspaceId: string,
  filters: Record<string, unknown> = {},
  page: number,
  pageSize: number
) => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // `panel` may carry several values as a comma-joined list so a single filter
  // option can cover a group of panel types; `search` substring-matches the one
  // column `searchField` names, so a search never spills across columns; `ids`
  // narrows to a pre-resolved set (the Activity filter, which depends on
  // conversation state and so can't be expressed as a column match). The rest
  // are exact-match.
  const { panel, search, searchField, ids, ...restFilters } = filters;
  const cleanFilters = Object.fromEntries(
    Object.entries(restFilters).filter(([_, value]) => value != null)
  );

  let query = supabase
    .from('respondents')
    .select('*', { count: 'exact' })
    .eq('workspace_id', workspaceId)
    .eq('deleted', false)
    .match(cleanFilters);

  if (typeof search === 'string' && search.trim()) {
    const field = parseRespondentSearchField(
      typeof searchField === 'string' ? searchField : null
    );
    // Numbers are stored in E.164, so a phone search typed the way people write
    // one — (541) 233-9225 — only matches once it is reduced to its digits. A
    // search with no digits at all is matched as typed rather than as an empty
    // pattern.
    const term =
      field === 'phone'
        ? search.replace(/\D/g, '') || search.trim()
        : search.trim();
    // %, _ and \ carry meaning inside a LIKE pattern; escaped, a search for
    // john_doe matches only john_doe rather than any john?doe.
    const escaped = term.replace(/[%_\\]/g, '\\$&');
    query = query.ilike(RESPONDENT_SEARCH_COLUMNS[field], `%${escaped}%`);
  }

  if (Array.isArray(ids)) {
    // An empty result set must stay empty rather than falling through unfiltered.
    if (ids.length === 0) return { data: [], total: 0 };
    query = query.in('id', ids);
  }

  if (panel != null) {
    const panelValues = String(panel)
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
    if (panelValues.length === 1) {
      query = query.eq('panel', panelValues[0]);
    } else if (panelValues.length > 1) {
      query = query.in('panel', panelValues);
    }
  }

  const { data, count } = await query
    .order('id', { ascending: false })
    .range(from, to)
    .throwOnError();
  return {
    data: data as unknown as Respondent[] | null,
    total: count || 0
  };
};

export const getRespondentByConversationId = async (
  supabase: SupabaseClient,
  conversationId: number
): Promise<Respondent | null> => {
  console.log('Getting respondent by conversationId:', conversationId);
  const { data: conversation } = await supabase
    .from('conversations')
    .select('respondent_id')
    .eq('id', conversationId)
    .throwOnError()
    .single();

  if (!conversation?.respondent_id) {
    return null;
  }

  // A deleted respondent reads as no respondent at all, matching how the
  // interview runtime treats them.
  const { data: respondent } = await supabase
    .from('respondents')
    .select('*')
    .eq('id', conversation.respondent_id)
    .eq('deleted', false)
    .throwOnError()
    .maybeSingle();

  return respondent as unknown as Respondent | null;
};

/**
 * Collects the query params seen across each respondent's conversations, keyed
 * by respondent id. Values are merged latest-first so the most recent
 * conversation wins per param key. Deleted/hidden conversations are excluded.
 * Backs the per-query-param columns in the respondents CSV export.
 */
export const getQueryParamsForRespondents = async (
  supabase: SupabaseClient,
  respondentIds: number[]
): Promise<Record<number, Record<string, string>>> => {
  const byRespondent: Record<number, Record<string, string>> = {};
  if (respondentIds.length === 0) return byRespondent;

  const { data } = await supabase
    .from('conversations')
    .select('respondent_id, query_params, created_at')
    .in('respondent_id', respondentIds)
    .eq('deleted', false)
    .eq('hidden', false)
    .order('created_at', { ascending: true })
    .throwOnError();

  (data ?? []).forEach((row) => {
    const respondentId = row.respondent_id as number | null;
    const params = row.query_params as Record<string, unknown> | null;
    if (respondentId == null || !params) return;
    const merged = byRespondent[respondentId] ?? {};
    Object.entries(params).forEach(([key, value]) => {
      merged[key] = value == null ? '' : String(value);
    });
    byRespondent[respondentId] = merged;
  });

  return byRespondent;
};

export interface RespondentImportResult {
  created: number;
  updated: number;
}

/** Rows per insert/upsert request when importing. */
const WRITE_CHUNK = 500;
/** Values per `in (...)` lookup, kept short enough for the request URL. */
const LOOKUP_CHUNK = 200;

/** The respondent columns an import writes. */
type ImportableColumns = Pick<
  Respondent,
  'full_name' | 'email' | 'phone_no' | 'external_context'
>;

const draftColumns = (draft: RespondentDraft): Partial<ImportableColumns> => {
  const columns: Partial<ImportableColumns> = {};
  if (draft.fullName) columns.full_name = draft.fullName;
  if (draft.email) columns.email = draft.email;
  if (draft.phoneNo) columns.phone_no = draft.phoneNo;
  if (draft.externalContext) columns.external_context = draft.externalContext;
  return columns;
};

/**
 * Adds respondents, looking each draft up among the workspace's custom-panel
 * rows to decide between creating one and updating one.
 *
 * Drafts match on panelist id alone: a draft whose id was generated rather than
 * supplied matches nothing and always creates a new person, no matter what else
 * it carries. An update writes only the fields the draft carries, leaving
 * anything the CSV left blank as it was.
 *
 * Deleted respondents are matched too and restored, since the unique index
 * spans them and their uid would otherwise be unusable. A restore counts toward
 * `created` rather than `updated` — the row is reused, but the person really is
 * being added back to a panel they were off, and reporting that as a quiet
 * update would hide a delete being reversed.
 *
 * CAUTION: a restore brings the whole original row back, not just the columns
 * in the file. Their previous name, email, phone and context survive anything
 * the import leaves blank, and every conversation, interview open and survey
 * response still points at that id, so the restored profile carries all of the
 * earlier person's transcripts and PII.
 *
 * Nothing here can tell whether the uid still means the same human. A workspace
 * that recycles ids — a seat number, an employee code, a CRM id reassigned
 * after churn — will hand the second person the first person's history and
 * personal data under their own name. The uid is treated as the identity
 * because it is the only identity there is; the risk is the customer's to
 * manage until deletion is made irreversible.
 */
export const importRespondents = async (
  supabase: SupabaseClient,
  workspaceId: string,
  drafts: RespondentDraft[]
): Promise<RespondentImportResult> => {
  if (drafts.length === 0) return { created: 0, updated: 0 };

  const panel = PanelType.CUSTOM;
  const suppliedIds = drafts
    .filter((d) => !d.generatedId)
    .map((d) => d.panelistId);

  const existingRows: (ImportableColumns & {
    id: number;
    panelist_id: string;
    deleted: boolean;
  })[] = [];
  const selectColumns =
    'id, panelist_id, deleted, full_name, email, phone_no, external_context';

  for (let start = 0; start < suppliedIds.length; start += LOOKUP_CHUNK) {
    const { data } = await supabase
      .from('respondents')
      .select(selectColumns)
      .eq('workspace_id', workspaceId)
      .eq('panel', panel)
      .in('panelist_id', suppliedIds.slice(start, start + LOOKUP_CHUNK))
      .throwOnError();
    existingRows.push(...((data ?? []) as unknown as typeof existingRows));
  }

  const byPanelistId = new Map(
    existingRows.map((row) => [row.panelist_id, row])
  );

  const inserts: Record<string, unknown>[] = [];
  const updates: Record<string, unknown>[] = [];
  const claimed = new Set<number>();
  // Restores are reported as additions: the person was off the panel, and to
  // whoever uploaded the file they have just been added.
  let restored = 0;

  drafts.forEach((draft) => {
    const match = draft.generatedId
      ? undefined
      : byPanelistId.get(draft.panelistId);

    // Two drafts resolving to the same row would collide in one upsert batch;
    // the second is inserted as its own respondent instead.
    if (match && !claimed.has(match.id)) {
      claimed.add(match.id);
      if (match.deleted) restored += 1;
      updates.push({
        id: match.id,
        workspace_id: workspaceId,
        panel,
        panelist_id: match.panelist_id,
        // A deleted respondent still owns its uid under the unique index, so
        // re-adding one restores the row rather than failing to insert.
        deleted: false,
        full_name: match.full_name,
        email: match.email,
        phone_no: match.phone_no,
        external_context: match.external_context,
        ...draftColumns(draft)
      });
      return;
    }

    inserts.push({
      workspace_id: workspaceId,
      panel,
      panelist_id: draft.panelistId,
      ...draftColumns(draft)
    });
  });

  // Written in batches so a large file doesn't go out as one oversized request.
  for (let start = 0; start < inserts.length; start += WRITE_CHUNK) {
    await supabase
      .from('respondents')
      .insert(inserts.slice(start, start + WRITE_CHUNK))
      .throwOnError();
  }
  for (let start = 0; start < updates.length; start += WRITE_CHUNK) {
    await supabase
      .from('respondents')
      .upsert(updates.slice(start, start + WRITE_CHUNK))
      .throwOnError();
  }

  return {
    created: inserts.length + restored,
    updated: updates.length - restored
  };
};

/**
 * Soft-deletes a respondent. The row stays for the conversations that reference
 * it and for the admin views, but every customer-facing read filters it out and
 * an interview they take no longer resolves to them.
 *
 * One row is the whole person: a panelist id holds a single row per workspace,
 * because respondents are never stored under the RESPONDENT_B2B/B2C sub-panels
 * (see the invariant on those enum members). Were that to change, deleting by
 * id alone would leave the sibling rows live and the person would still be
 * recognised through them.
 */
export const deleteRespondent = async (
  supabase: SupabaseClient,
  respondentId: number
): Promise<void> => {
  await supabase
    .from('respondents')
    .update({ deleted: true })
    .eq('id', respondentId)
    .throwOnError();
};

/**
 * Updates a respondent row by id. A `panel` in the payload is normalized, so
 * this path can't move someone onto a RESPONDENT_B2B/B2C row either — see the
 * invariant on those enum members.
 */
export const updateRespondent = async (
  supabase: SupabaseClient,
  respondentId: number,
  data: Partial<Respondent>
): Promise<void> => {
  const payload =
    data.panel == null
      ? data
      : { ...data, panel: normalizeForRespondentPanel(Number(data.panel)) };

  await supabase
    .from('respondents')
    .update(payload)
    .eq('id', respondentId)
    .throwOnError();
};

/**
 * The respondent a panel link identifies, as the row id to store on the
 * conversation: null when the link carries no supported userid param, when the
 * interview has no workspace, or when the workspace removed that person.
 *
 * Best-effort by contract: none of the flows that call this (room creation,
 * phone allocation, screener completion) may fail over attribution, so any
 * error is logged under `source` and resolves to null instead of propagating.
 */
export const resolveRespondentId = async (
  supabase: SupabaseClient,
  interviewId: number,
  queryParams: Record<string, unknown>,
  source: string
): Promise<number | null> => {
  const match = getPanelistIdMatch(queryParams);
  if (!match) return null;

  try {
    const workspaceId = await getInterviewWorkspaceId(supabase, interviewId);
    if (!workspaceId) return null;
    return await getOrCreateRespondent(
      supabase,
      match.panel,
      match.panelistId,
      workspaceId
    );
  } catch (error) {
    console.error(
      '[%s] failed to resolve respondent for interview %s',
      source,
      interviewId,
      error
    );
    return null;
  }
};
